/**
 * Role-aware footer navigation link groups.
 */

import {
  ROUTE_ADMIN,
  ROUTE_CLIENT_DASHBOARD,
  ROUTE_OWNER_DASHBOARD,
  isGuestContext,
  showsOwnerNavigation,
} from './roleContext';

export const FOOTER_CONTEXT_ANONYMOUS = 'anonymous';
export const FOOTER_CONTEXT_CLIENT = 'client';
export const FOOTER_CONTEXT_OWNER = 'owner';
export const FOOTER_CONTEXT_ADMIN = 'admin';

export const FOOTER_TEST_ID = 'footer';

/** Hrefs that must never appear in client/guest footer. */
export const CLIENT_FORBIDDEN_FOOTER_HREFS = [
  '/register?role=owner',
  '/pricing',
  '/owner/plans',
  '/owner/dashboard',
  '/owner/listings',
  '/owner/listings/new',
];

/** Label keys associated with owner CTAs in footer. */
export const CLIENT_FORBIDDEN_FOOTER_LABEL_KEYS = [
  'footer.list_property',
  'footer.owner_login',
  'footer.owner_pricing',
  'footer.for_owners',
];

function link(href, labelKey, defaultLabel, testId) {
  return { href, labelKey, defaultLabel, testId };
}

const ANONYMOUS_QUICK_LINKS = [
  link('/listings', 'nav.listings', 'Browse Homes', 'footer-anon-browse'),
  link('/about', 'footer.about', 'About Us', 'footer-anon-about'),
  link('/locations', 'nav.locations', 'Locations', 'footer-anon-locations'),
  // Batch 32: surface public differentiators that were routed but had no inbound nav.
  link('/tla-calculator', 'footer.tla_calculator', 'TLA Calculator', 'footer-anon-tla-calculator'),
  link('/local-favorites', 'footer.local_favorites', 'Local Favorites', 'footer-anon-local-favorites'),
  link('/faq', 'footer.faq_support', 'FAQ & Support', 'footer-anon-faq'),
  link('/contact', 'footer.contact_form', 'Contact', 'footer-anon-contact'),
];

const ANONYMOUS_OWNER_LINKS = [
  link('/register?role=owner', 'footer.list_property', 'List Your Property', 'footer-anon-list-property'),
  link('/login', 'footer.owner_login', 'Owner Login', 'footer-anon-owner-login'),
  link('/pricing', 'footer.owner_pricing', 'Owner Pricing', 'footer-anon-owner-pricing'),
];

const CLIENT_PRIMARY_LINKS = [
  link('/listings', 'client.nav_browse_homes', 'Browse Homes', 'footer-client-browse'),
  link(ROUTE_CLIENT_DASHBOARD, 'nav.client_home_search', 'My Home Search', 'footer-client-dashboard'),
  link('/bookings', 'nav.my_bookings', 'My Booking Requests', 'footer-client-bookings'),
  link('/notifications', 'nav.inbox', 'Inbox', 'footer-client-inbox'),
  link('/favorites', 'nav.my_favorites', 'Favorites', 'footer-client-favorites'),
  link('/settings', 'nav.settings', 'Settings', 'footer-client-settings'),
];

const CLIENT_SUPPORT_LINKS = [
  link('/faq', 'footer.faq_support', 'Help / FAQ', 'footer-client-faq'),
  link('/contact', 'footer.contact_support', 'Contact Support', 'footer-client-contact'),
  link('/privacy', 'footer.privacy_data', 'Privacy & Data', 'footer-client-privacy'),
  link('/pcs-checklist', 'footer.tutorials_getting_started', 'Tutorials / Getting Started', 'footer-client-tutorials'),
];

const OWNER_PRIMARY_LINKS = [
  link(ROUTE_OWNER_DASHBOARD, 'nav.owner_dashboard', 'Owner Dashboard', 'footer-owner-dashboard'),
  link('/owner/listings', 'footer.my_listings', 'My Listings', 'footer-owner-listings'),
  link('/owner/listings/new', 'footer.add_listing', 'Add Listing', 'footer-owner-add-listing'),
  link('/owner/dashboard', 'footer.incoming_requests', 'Incoming Requests', 'footer-owner-requests'),
  link('/owner/plans', 'footer.owner_plans', 'Owner Plans', 'footer-owner-plans'),
  link('/listings', 'client.nav_browse_homes', 'Browse Homes', 'footer-owner-browse'),
];

const OWNER_SUPPORT_LINKS = [
  link('/faq', 'footer.faq_support', 'Help / Support', 'footer-owner-faq'),
  link('/contact', 'footer.contact_support', 'Contact Support', 'footer-owner-contact'),
];

const ADMIN_PRIMARY_LINKS = [
  link('/listings', 'nav.listings', 'Browse Homes', 'footer-admin-browse'),
  link('/about', 'footer.about', 'About Us', 'footer-admin-about'),
  link('/locations', 'nav.locations', 'Locations', 'footer-admin-locations'),
  link('/tla-calculator', 'footer.tla_calculator', 'TLA Calculator', 'footer-admin-tla-calculator'),
  link(ROUTE_ADMIN, 'nav.admin_dashboard', 'Admin Dashboard', 'footer-admin-dashboard'),
  link('/faq', 'footer.faq_support', 'FAQ & Support', 'footer-admin-faq'),
  link('/contact', 'footer.contact_form', 'Contact', 'footer-admin-contact'),
];

export function resolveFooterContext(roleState) {
  const { user, isAdmin, viewAsRole } = roleState;
  if (!user) {
    return FOOTER_CONTEXT_ANONYMOUS;
  }
  if (isGuestContext(roleState)) {
    return FOOTER_CONTEXT_CLIENT;
  }
  if (isAdmin && !viewAsRole) {
    return FOOTER_CONTEXT_ADMIN;
  }
  if (showsOwnerNavigation(roleState)) {
    return FOOTER_CONTEXT_OWNER;
  }
  return FOOTER_CONTEXT_CLIENT;
}

export function resolveFooterSections(roleState) {
  const context = resolveFooterContext(roleState);

  if (context === FOOTER_CONTEXT_ANONYMOUS) {
    return [
      {
        id: 'quick',
        titleKey: 'footer.quick_links',
        defaultTitle: 'Quick Links',
        links: ANONYMOUS_QUICK_LINKS,
      },
      {
        id: 'owners',
        titleKey: 'footer.for_owners',
        defaultTitle: 'For Owners',
        links: ANONYMOUS_OWNER_LINKS,
      },
    ];
  }

  if (context === FOOTER_CONTEXT_CLIENT) {
    return [
      {
        id: 'client',
        titleKey: 'footer.my_home_search',
        defaultTitle: 'My Home Search',
        links: CLIENT_PRIMARY_LINKS,
      },
      {
        id: 'support',
        titleKey: 'footer.help_support',
        defaultTitle: 'Help & Support',
        links: CLIENT_SUPPORT_LINKS,
      },
    ];
  }

  if (context === FOOTER_CONTEXT_OWNER) {
    return [
      {
        id: 'owner',
        titleKey: 'footer.owner_tools',
        defaultTitle: 'Owner Tools',
        links: OWNER_PRIMARY_LINKS,
      },
      {
        id: 'support',
        titleKey: 'footer.help_support',
        defaultTitle: 'Help & Support',
        links: OWNER_SUPPORT_LINKS,
      },
    ];
  }

  return [
    {
      id: 'platform',
      titleKey: 'footer.platform_links',
      defaultTitle: 'Platform',
      links: ADMIN_PRIMARY_LINKS,
    },
  ];
}

export function footerLinkHrefs(roleState) {
  return resolveFooterSections(roleState).flatMap((section) => section.links.map((item) => item.href));
}

export function footerHasForbiddenClientLinks(roleState) {
  if (resolveFooterContext(roleState) !== FOOTER_CONTEXT_CLIENT) {
    return false;
  }
  const hrefs = footerLinkHrefs(roleState);
  return CLIENT_FORBIDDEN_FOOTER_HREFS.some((forbidden) => hrefs.includes(forbidden));
}

export function anonymousFooterIncludesOwnerMarketing(roleState) {
  if (resolveFooterContext(roleState) !== FOOTER_CONTEXT_ANONYMOUS) {
    return false;
  }
  const hrefs = footerLinkHrefs(roleState);
  return hrefs.includes('/register?role=owner') && hrefs.includes('/pricing');
}

export function ownerFooterIncludesOwnerTools(roleState) {
  if (resolveFooterContext(roleState) !== FOOTER_CONTEXT_OWNER) {
    return false;
  }
  const hrefs = footerLinkHrefs(roleState);
  return hrefs.includes(ROUTE_OWNER_DASHBOARD) && hrefs.includes('/owner/plans');
}
